import React from "react";
import {
  DataGrid,
  GridOverlay,
  GridPagination,
  GridColDef,
} from "@mui/x-data-grid";
import CustomPaginationActions from "./CustomPaginationActions";
import { Box, Typography, Button, useTheme, useMediaQuery, CircularProgress } from "@mui/material";
import ClassToolBar from "./ClassToolBar";
import AllClassesMobileCardView from "./AllClassesMobileCardView";
import { AllClassesDataGridProps } from "../interfaces/allClasses.interface";
import themestyle from "../theme";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const AllClassesDataGrid: React.FC<AllClassesDataGridProps> = ({
  rows,
  columns,
  loading = false,
  searchQuery,
  onSearchQueryChange,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  pageSizeOptions = [5, 10, 20],
  getRowId,
  instructorValue,
  dayValue,
  classTypeValue,
  onInstructorChange,
  onDayChange,
  onClassTypeChange,
  onMobileView,
  onMobileEdit,
  onMobileDelete,
  onMobileAttendance,
  classOptions,
  classOptionsLoading,
  activeTab,
  onTabChange,
  instructorOptions,
  selectedIds = [],
  onToggleSelectId,
  onSelectAll,
  onDeselectAll,
  selectAllPages,
  onBulkDeleteClick,
  totalCount = 0,
  canViewPreviousActions = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  function CustomNoRowsOverlay() {
    if (loading) return null;
    return (
      <GridOverlay>
        <Typography
          variant={isMobile ? "body2" : "h6"}
          component="p"
          gutterBottom
          sx={{ mt: 1, fontSize: "0.875rem" }}
        >
          {rows.length === 0 && searchQuery ? "No match found" : "No rows"}
        </Typography>
      </GridOverlay>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ width: "100%", mt: 1, position: "relative" }}>
        <ClassToolBar
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
          instructorValue={instructorValue}
          dayValue={dayValue}
          classTypeValue={classTypeValue}
          onInstructorChange={onInstructorChange}
          onDayChange={onDayChange}
          onClassTypeChange={onClassTypeChange}
          classOptions={classOptions}
          classOptionsLoading={classOptionsLoading}
          instructorOptions={instructorOptions}
        />
        {onTabChange && (
          <Box
            sx={{
              display: "flex",
              gap:1,
              px: 1,
              borderBottom: "1px solid #E5E7EB",
              backgroundColor: "transparent",
              mb: 1,
              mt: 1,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {[
              { id: "current", label: "Today's Classes" },
              { id: "upcoming", label: "Upcoming Classes" },
              { id: "previous", label: "Previous Classes" },
            ].map((tab) => (
              <Typography
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: activeTab === tab.id ? themestyle.colors.activeGreen : "#6B7280",
                  cursor: "pointer",
                  pb: 0.5,
                  borderBottom:
                    activeTab === tab.id
                      ? `2px solid ${themestyle.colors.activeGreen}`
                      : "2px solid transparent",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                {tab.label}
              </Typography>
            ))}
          </Box>
        )}
        <AllClassesMobileCardView
          rows={rows}
          loading={loading}
          onView={onMobileView}
          onEdit={onMobileEdit}
          onDelete={onMobileDelete}
          onAttendance={onMobileAttendance}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={rowCount}
          activeTab={activeTab}
          canViewPreviousActions={canViewPreviousActions}
        />
      </Box>
    );
  }

  const showSelectionBar = activeTab === "upcoming" && (selectedIds.length > 0 || selectAllPages);
  const selectionCount = selectAllPages ? totalCount : selectedIds.length;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: {
          xs: "calc(100vh - 180px)",
          sm: "calc(65vh - 220px)",
          md: "calc(90vh - 220px)",
        },
        minHeight: "450px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ClassToolBar
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        instructorValue={instructorValue}
        dayValue={dayValue}
        classTypeValue={classTypeValue}
        onInstructorChange={onInstructorChange}
        onDayChange={onDayChange}
        onClassTypeChange={onClassTypeChange}
        classOptions={classOptions}
        classOptionsLoading={classOptionsLoading}
        instructorOptions={instructorOptions}
      />
      {!isMobile && onTabChange && (
        <Box
          sx={{
            display: "flex",
            gap: 4,
            px: 3,
            borderBottom: "1px solid #E5E7EB",
            backgroundColor: "transparent",
            mb: 1,
            mt: 1,
          }}
        >
          {[
            { id: "current", label: "Today's Classes" },
            { id: "upcoming", label: "Upcoming Classes" },
            { id: "previous", label: "Previous Classes" },
          ].map((tab) => (
            <Typography
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: activeTab === tab.id ? themestyle.colors.activeGreen : "#6B7280",
                cursor: "pointer",
                pb: 1,
                borderBottom: activeTab === tab.id ? `3px solid ${themestyle.colors.activeGreen}` : "3px solid transparent",
                transition: "all 0.3s ease",
                "&:hover": { color: themestyle.colors.activeGreen },
              }}
            >
              {tab.label}
            </Typography>
          ))}
        </Box>
      )}
      {showSelectionBar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            mx: 0,
            mb: 0.5,
            borderRadius: "8px",
            backgroundColor: showSelectionBar ? "rgba(229,57,53,0.06)" : "transparent",
            border: showSelectionBar ? "1px solid rgba(229,57,53,0.15)" : "1px solid transparent",
            minHeight: "44px",
            transition: "all 0.2s ease",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {showSelectionBar && (
              <>
                <FiberManualRecordIcon sx={{ color: themestyle.colors.error, fontSize: 10 }} />
                <Typography
                  sx={{
                    color: themestyle.colors.error,
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                >
                  {selectionCount} {selectionCount === 1 ? "class" : "classes"} selected
                </Typography>
                <Box sx={{ width: "1px", height: "16px", backgroundColor: "rgba(229,57,53,0.3)" }} />
                <Typography
                  onClick={onDeselectAll}
                  sx={{
                    color: themestyle.colors.error,
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": { opacity: 0.75 },
                  }}
                >
                  {selectionCount === 1 ? "Deselect" : "Deselect All"}
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onSelectAll}
              sx={{
                borderColor: "#D1D5DB",
                color: "#374151",
                fontWeight: 600,
                fontSize: "13px",
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 0.6,
                "&:hover": {
                  borderColor: "#374151",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              Select All
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              disabled={selectionCount === 0}
              onClick={onBulkDeleteClick}
              sx={{
                backgroundColor: selectionCount > 0 ? themestyle.colors.error : "#D1D5DB",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "13px",
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 0.6,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: selectionCount > 0 ? "#C62828" : "#D1D5DB",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#D1D5DB",
                  color: "#9CA3AF",
                },
              }}
            >
              Delete Selected
            </Button>
          </Box>
        </Box>
      )}
      <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          getRowSpacing={(params) => ({
            top: params.isFirstVisible ? 0 : 4,
            bottom: params.isLastVisible ? 0 : 4,
          })}
          components={{
            NoRowsOverlay: CustomNoRowsOverlay,
            Pagination: GridPagination,
          }}
          componentsProps={{
            pagination: { ActionsComponent: CustomPaginationActions } as any,
          }}
          paginationModel={paginationModel}
          rowCount={activeTab === "current" ? rowCount : rows.length}
          paginationMode={activeTab === "current" ? "server" : "client"}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          getRowClassName={(params: any) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
          }
          disableRowSelectionOnClick
          getRowId={getRowId}
          sx={{
            backgroundColor: themestyle.colors.white,
            border: "none",
            fontFamily: "Poppins, Roboto, sans-serif",
            flex: 1,
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: themestyle.colors.white,
              paddingBottom: 0,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "#F9F9FB",
              border: "1px solid #F0F0F0",
            },
            "& .MuiDataGrid-row.even-row": { backgroundColor: "#F9F9FB" },
            "& .MuiDataGrid-row.odd-row": { backgroundColor: "#F9F9FB" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F9F9FB",
              borderBottom: "1px solid #ECECEC",
              minHeight: 52,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "14px",
              color: "#2B2B2B",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              fontSize: "13px",
              color: "#1F2937",
              px: 1,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: themestyle.colors.white,
              borderTop: "1px solid #ECECEC",
              minHeight: 56,
              marginTop: 0,
            },
            "& .MuiTablePagination-root": {
              alignItems: "center",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: 13,
                  color: "#6B7280",
                },
              "& .MuiTablePagination-displayedRows": {
                marginRight: "16px",
              },
              "& .MuiTablePagination-actions": {
                display: "flex",
                alignItems: "center",
                gap: "12px",
              },
            },
          }}
          filterMode="server"
          // Client-side sorting so column header clicks re-order the loaded rows
          // immediately (no server sort param / handler was ever wired up).
          sortingMode="client"
        />

        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress color="success" />
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default AllClassesDataGrid;
